import { getSketchObjectDepthProperty, getSketchObjectWidthProperty } from '@pointorama/potree-shared';
import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame, type SketchObjectResizeHandleLayout } from '../types';
import { at, pushPolyline, pushSegment } from '../glyphs/frame';
import { getNumberParamProperty, type ParamValues } from '../paramSchema';
import { MIN_FLIGHT_RUN, STEP_EPS, pushWalkArrow, splitFlightSteps } from './stairCommon';
import { t } from '../../translation/markers';

/** Shortest a flight can get (m) — also the leg params' validation min. */
const MIN_LEG = MIN_FLIGHT_RUN;

// Legs + flight width + step + step count sit ABOVE "distance to wall"
// (order 12), right after the shared width/depth rows (10-11).
const PARAMS = {
  leftLegLength: { kind: 'float', label: t('left leg'), default: 1.5, min: MIN_LEG, decimals: 2, distance: true, order: 11.1 },
  rightLegLength: { kind: 'float', label: t('right leg'), default: 1.5, min: MIN_LEG, decimals: 2, distance: true, order: 11.2 },
  flightWidth: { kind: 'float', label: t('flight width'), default: 0.9, min: 0.5, max: 2.0, decimals: 2, distance: true, order: 11.3 },
  treadDepth: { kind: 'float', label: t('step'), default: 0.25, min: 0.15, max: 0.4, decimals: 2, distance: true, order: 11.4 },
  stepCount: { kind: 'int', label: t('number of steps'), default: 12, min: 2, order: 11.5 },
  reversed: { kind: 'boolean', label: t('reverse direction'), default: false },
} as const;

/** Effective flight width: at most half the footprint width (two flights
 *  side by side). */
function effectiveFlightWidth(width: number, flightWidth: number): number {
  return Math.max(0.1, Math.min(flightWidth, width / 2));
}

/** The two legs clamped to the drawable range of the current footprint. */
function effectiveLegs(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>, fw: number): { left: number; right: number } {
  const longest = Math.max(0.05, frame.depth - fw);
  return {
    left: Math.min(Math.max(MIN_LEG, params.leftLegLength), longest),
    right: Math.min(Math.max(MIN_LEG, params.rightLegLength), longest),
  };
}

/**
 * U-shaped (half-turn) stair: hybrid LINE + FREE placement like the straight
 * stair — the drawn run is the footprint width (the side holding both flight
 * ends). Two parallel flights of INDEPENDENT length hang from a landing
 * strip on the far (+Y) edge; any width beyond two flights becomes the open
 * stairwell between them (the outline steps around it — no edge crosses the
 * well). The footprint depth always equals the landing plus the LONGER leg
 * (kept by syncParams). Treads split the synced step total over the legs by
 * their run share; `reversed` flips the walk direction.
 */
function buildStairUGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  const fw = effectiveFlightWidth(frame.width, params.flightWidth);
  const { left, right } = effectiveLegs(frame, params, fw);
  const nLanding = hd - fw;
  const nLeft = nLanding - left;
  const nRight = nLanding - right;

  // Stepped U outline — with touching flights (fw == width/2) the two inner
  // edges coincide and become the center divider.
  pushPolyline(out, frame, [
    [-hw, hd],
    [hw, hd],
    [hw, nRight],
    [hw - fw, nRight],
    [hw - fw, nLanding],
    [-hw + fw, nLanding],
    [-hw + fw, nLeft],
    [-hw, nLeft],
    [-hw, hd],
  ]);

  // Landing boundaries across both flights (the well part is in the outline).
  pushSegment(out, at(frame, -hw, nLanding), at(frame, -hw + fw, nLanding), frame.z);
  pushSegment(out, at(frame, hw - fw, nLanding), at(frame, hw, nLanding), frame.z);

  const { countA, countB } = splitFlightSteps(left, right, params.treadDepth);
  // Left flight treads; the flight's open end and the landing boundary are
  // already drawn.
  for (let k = 1; k < countA; k++) {
    const n = nLeft + (k * left) / countA;
    pushSegment(out, at(frame, -hw, n), at(frame, -hw + fw, n), frame.z);
  }
  // Right flight treads.
  for (let k = 1; k < countB; k++) {
    const n = nLanding - (k * right) / countB;
    pushSegment(out, at(frame, hw - fw, n), at(frame, hw, n), frame.z);
  }

  // Walk line: up the left centerline, across the landing, down the right.
  const insetLeft = Math.min(0.2, 0.25 * left);
  const insetRight = Math.min(0.2, 0.25 * right);
  const headSize = Math.min(0.15, fw * 0.3);
  pushWalkArrow(
    out,
    frame,
    [
      [-hw + fw / 2, nLeft + insetLeft],
      [-hw + fw / 2, hd - fw / 2],
      [hw - fw / 2, hd - fw / 2],
      [hw - fw / 2, nRight + insetRight],
    ],
    params.reversed,
    headSize,
  );

  return out;
}

const SYNC_KEYS: readonly string[] = ['width', 'depth', 'flightWidth', 'leftLegLength', 'rightLegLength', 'treadDepth', 'stepCount'];

/**
 * Coupled-param sync of the U stair (same convergence contract as stair.ts —
 * every write no-ops once values agree):
 *  - footprint depth changed (panel / gizmo scale) → both legs shift by the
 *    same amount so the longer one fills the box again, difference preserved;
 *  - legs / flight width / width changed → depth := landing + longer leg;
 *  - number of steps changed → step := combined run / count;
 *  - anything else → count := round(combined run / step).
 */
function syncStairUParams(objectId: string, changedKey: string): void {
  if (!SYNC_KEYS.includes(changedKey)) return;
  let width: number;
  let depthProperty;
  try {
    width = getSketchObjectWidthProperty(objectId).value ?? 0;
    depthProperty = getSketchObjectDepthProperty(objectId);
  } catch {
    return; // property service not ready
  }
  const depth = depthProperty.value ?? 0;
  const fw = effectiveFlightWidth(width, getNumberParamProperty('stair_u', 'flightWidth', objectId).value ?? PARAMS.flightWidth.default);
  const leftProperty = getNumberParamProperty('stair_u', 'leftLegLength', objectId);
  const rightProperty = getNumberParamProperty('stair_u', 'rightLegLength', objectId);
  const left = Math.max(MIN_LEG, leftProperty.value ?? PARAMS.leftLegLength.default);
  const right = Math.max(MIN_LEG, rightProperty.value ?? PARAMS.rightLegLength.default);

  if (changedKey === 'depth') {
    const delta = Math.max(MIN_LEG, depth - fw) - Math.max(left, right);
    if (Math.abs(delta) > STEP_EPS) {
      // The leg writes re-enter this sync and settle depth + steps below.
      leftProperty.value = Math.max(MIN_LEG, left + delta);
      rightProperty.value = Math.max(MIN_LEG, right + delta);
    }
    return;
  }

  if (changedKey !== 'treadDepth' && changedKey !== 'stepCount') {
    const wantDepth = fw + Math.max(left, right);
    if (Math.abs(depth - wantDepth) > STEP_EPS) depthProperty.value = wantDepth;
  }

  const run = left + right;
  if (run <= 0) return;
  const stepProperty = getNumberParamProperty('stair_u', 'treadDepth', objectId);
  const countProperty = getNumberParamProperty('stair_u', 'stepCount', objectId);
  if (changedKey === 'stepCount') {
    const count = Math.max(2, Math.round(countProperty.value ?? 2));
    const step = run / count;
    if (Math.abs((stepProperty.value ?? 0) - step) > STEP_EPS) stepProperty.value = step;
  } else {
    const step = Math.max(0.05, stepProperty.value ?? 0.25);
    const count = Math.max(2, Math.round(run / step));
    if (countProperty.value !== count) countProperty.value = count;
  }
}

/** Depth-handle drag of ONE leg: the pulled distance measures from the box
 *  top (the pinned edge), so the leg is that minus the landing. Reads live
 *  values per move; syncParams keeps depth + steps in line with the write. */
function applyLegDrag(key: 'leftLegLength' | 'rightLegLength'): (objectId: string, pulledDepth: number) => number {
  const otherKey = key === 'leftLegLength' ? 'rightLegLength' : 'leftLegLength';
  return (objectId, pulledDepth) => {
    const width = getSketchObjectWidthProperty(objectId).value ?? 0;
    const fw = effectiveFlightWidth(width, getNumberParamProperty('stair_u', 'flightWidth', objectId).value ?? PARAMS.flightWidth.default);
    const other = Math.max(MIN_LEG, getNumberParamProperty('stair_u', otherKey, objectId).value ?? PARAMS[otherKey].default);
    const legProperty = getNumberParamProperty('stair_u', key, objectId);
    const leg = Math.max(MIN_LEG, pulledDepth - fw);
    if (Math.abs((legProperty.value ?? 0) - leg) > STEP_EPS) legProperty.value = leg;
    return fw + Math.max(leg, other);
  };
}

/** Depth-handle drag of the WHOLE bottom: both legs shift by the same amount
 *  so the longer one lands on the cursor, difference preserved. */
function applyBothLegsDrag(objectId: string, pulledDepth: number): number {
  const width = getSketchObjectWidthProperty(objectId).value ?? 0;
  const fw = effectiveFlightWidth(width, getNumberParamProperty('stair_u', 'flightWidth', objectId).value ?? PARAMS.flightWidth.default);
  const leftProperty = getNumberParamProperty('stair_u', 'leftLegLength', objectId);
  const rightProperty = getNumberParamProperty('stair_u', 'rightLegLength', objectId);
  const left = Math.max(MIN_LEG, leftProperty.value ?? PARAMS.leftLegLength.default);
  const right = Math.max(MIN_LEG, rightProperty.value ?? PARAMS.rightLegLength.default);
  const delta = Math.max(MIN_LEG, pulledDepth - fw) - Math.max(left, right);
  const newLeft = Math.max(MIN_LEG, left + delta);
  const newRight = Math.max(MIN_LEG, right + delta);
  if (Math.abs(left - newLeft) > STEP_EPS) leftProperty.value = newLeft;
  if (Math.abs(right - newRight) > STEP_EPS) rightProperty.value = newRight;
  return fw + Math.max(newLeft, newRight);
}

/**
 * Handle layout: the default run-end handles (mid-depth on the side edges)
 * already sit on the flights, and THREE depth handles cover the open side —
 * one on each flight's end dragging just that leg, one in the middle of the
 * bottom edge dragging both together. The landing edge stays pinned for all
 * of them.
 */
function stairUResizeHandles(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): SketchObjectResizeHandleLayout {
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  const fw = effectiveFlightWidth(frame.width, params.flightWidth);
  const { left, right } = effectiveLegs(frame, params, fw);
  const nLanding = hd - fw;
  return {
    depth: [
      { u: -hw + fw / 2, n: nLanding - left, side: -1, apply: applyLegDrag('leftLegLength') },
      { u: hw - fw / 2, n: nLanding - right, side: -1, apply: applyLegDrag('rightLegLength') },
      { u: 0, n: -hd, side: -1, apply: applyBothLegsDrag },
    ],
  };
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'stair_u',
    label: t('U-shaped stair'),
    category: SketchObjectCategory.FIXTURES,
    anchor: SketchObjectAnchorKind.LINE,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 2.0, depth: 2.4 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildStairUGlyph },
    resizeHandles: stairUResizeHandles,
    syncParams: syncStairUParams,
  }),
);
